import React, { useEffect, useMemo, useState } from "react";
import { Loader2, CheckCircle2, XCircle, Eye, BotMessageSquare, FileText, Calendar, ExternalLink } from "lucide-react";
import { adminApi } from "../../lib/api";
import { Button } from "../ui/button";
import { useToast } from "../../hooks/use-toast";
import DOMPurify from "dompurify";

const formatDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return s; }
};

const ReviewerPanel = ({ token }) => {
  const a = useMemo(() => adminApi(token), [token]);
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await a.reviewQueue()); }
    catch (err) { toast({ title: "Fehler", description: err?.response?.data?.detail || "Keine Berechtigung", variant: "destructive" }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onApprove = async (it) => {
    setBusy(it.id);
    try {
      await a.approveArticle(it.id);
      toast({ title: "Freigegeben", description: it.title });
      if (preview?.id === it.id) setPreview(null);
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setBusy(null); }
  };

  const onReject = async (it) => {
    if (!window.confirm(`Draft "${it.title}" ablehnen? Bleibt als unverö.ffentlichter Draft erhalten.`)) return;
    setBusy(it.id);
    try {
      await a.rejectArticle(it.id);
      toast({ title: "Abgelehnt" });
      if (preview?.id === it.id) setPreview(null);
      await load();
    } catch (err) {
      toast({ title: "Fehler", description: err?.response?.data?.detail, variant: "destructive" });
    } finally { setBusy(null); }
  };

  return (
    <div data-testid="reviewer-panel" className="space-y-4">
      <div>
        <h2 className="flex items-center gap-2 text-xl font-bold text-white">
          <FileText className="h-5 w-5 text-[#b39bff]" /> Reviewer-Dashboard
        </h2>
        <p className="mt-1 text-sm text-zinc-400">
          {items.length} Draft{items.length === 1 ? "" : "s"} warten auf Freigabe. KI-Imports und manuelle Drafts erscheinen hier.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/[0.04] p-10 text-center">
          <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-400" />
          <div className="text-sm font-semibold text-white">Alles im grünen Bereich</div>
          <div className="mt-1 text-xs text-zinc-500">Aktuell warten keine Drafts auf Freigabe.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_1fr]">
          {/* Liste */}
          <div className="space-y-2">
            {items.map((it) => {
              const isAi = !!it.ai_source_guid;
              const isPreview = preview?.id === it.id;
              return (
                <div
                  key={it.id}
                  data-testid={`review-item-${it.id}`}
                  className={`rounded-xl border p-4 transition-colors ${isPreview ? "border-[#7c4dff]/60 bg-[#7c4dff]/[0.06]" : "border-white/10 bg-[#0f141b] hover:border-white/25"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wider">
                        <span className="rounded-full border border-white/10 bg-[#10141b] px-2 py-0.5 text-zinc-400">{it.category || "News"}</span>
                        {isAi && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[#7c4dff]/30 bg-[#7c4dff]/10 px-2 py-0.5 text-[#b39bff]">
                            <BotMessageSquare className="h-3 w-3" /> KI · {it.ai_model || "?"}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 text-zinc-500">
                          <Calendar className="h-3 w-3" /> {formatDate(it.ai_imported_at || it.date)}
                        </span>
                      </div>
                      <div className="text-sm font-semibold text-white">{it.title}</div>
                      {it.excerpt && <div className="mt-1 line-clamp-2 text-xs text-zinc-400">{it.excerpt}</div>}
                      <div className="mt-1 text-[11px] text-zinc-500">Autor: {it.author || "—"}</div>
                    </div>
                    <div className="flex shrink-0 flex-col gap-1">
                      <Button onClick={() => setPreview(it)} className="btn-ghost rounded-lg px-2 py-1 text-xs" data-testid={`review-preview-${it.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center justify-end gap-2">
                    <Button onClick={() => onReject(it)} disabled={busy === it.id} className="btn-ghost rounded-lg px-3 py-1.5 text-xs text-rose-300 hover:border-rose-500/40" data-testid={`review-reject-${it.id}`}>
                      <XCircle className="mr-1 h-4 w-4" /> Ablehnen
                    </Button>
                    <Button onClick={() => onApprove(it)} disabled={busy === it.id} className="btn-primary rounded-lg px-3 py-1.5 text-xs" data-testid={`review-approve-${it.id}`}>
                      {busy === it.id ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Freigeben
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Preview */}
          <div className="hidden lg:block sticky top-4 self-start">
            {preview ? (
              <div className="rounded-xl border border-white/10 bg-[#0f141b] p-5" data-testid="review-preview-card">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Vorschau</div>
                  {preview.ai_source_url && (
                    <a href={preview.ai_source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[11px] text-[#b39bff] hover:underline">
                      Original <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                <h3 className="mt-2 text-lg font-bold text-white">{preview.title}</h3>
                {preview.excerpt && <p className="mt-1 text-sm italic text-zinc-400">{preview.excerpt}</p>}
                <div className="mt-4 max-h-[60vh] overflow-y-auto rounded-lg border border-white/5 bg-[#0a0d12] p-4">
                  <div
                    className="news-content"
                    style={{ "--news-font-size": "15px" }}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.content_html || "") }}
                  />
                </div>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-white/10 p-10 text-center text-sm text-zinc-500">
                <Eye className="mx-auto mb-2 h-6 w-6 opacity-50" /> Wähle links einen Draft zur Vorschau aus.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewerPanel;
